import { syncPropertyNames } from '@theme/roots-addon-properties';

/**
 * Blouse design module (snippets/roots-blouse-design.liquid).
 *
 * "As photographed" is the default and needs nothing else from the shopper.
 * "Choose a different design" reveals the design groups and makes one pick
 * per group required. Picking a sleeve (other than a "No sleeve" design)
 * reveals and requires the sleeve measurements. While an ancestor is inert (a closed add-on panel)
 * every field stays disabled; the add-on component calls `sync()` after it
 * opens or closes.
 */
class RootsBlouseDesign extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  /** @returns {'as-shown' | 'custom'} */
  get mode() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-design-mode]:checked'));
    return checked?.dataset.designMode === 'custom' ? 'custom' : 'as-shown';
  }

  get active() {
    return !this.closest('[inert]');
  }

  sync() {
    const active = this.active;
    const custom = this.mode === 'custom';

    this.dataset.mode = custom ? 'custom' : 'as-shown';
    this.querySelector('[data-design-panel]')?.toggleAttribute('hidden', !custom);

    for (const input of this.#inputs('[data-design-mode]')) {
      input.disabled = !active;
    }

    for (const input of this.#inputs('[data-design-field]')) {
      input.disabled = !active || !custom;
      input.required = active && custom;
    }

    const property = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-design-property]'));
    if (property) {
      property.disabled = !active;
      property.value = this.querySelector('[data-design-mode]:checked')?.getAttribute('value') ?? 'As photographed';
    }

    this.#showPicks();
    this.#syncSleeve();
  }

  /** Shows the sleeve measurements once a sleeve with an actual sleeve is picked. */
  #syncSleeve() {
    const sleeve = /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-design-field][data-property-name="Blouse sleeve design"]:checked')
    );
    const needed = this.active && this.mode === 'custom' && Boolean(sleeve) && !sleeve?.hasAttribute('data-design-sleeveless');

    this.querySelector('[data-design-sleeve-panel]')?.toggleAttribute('hidden', !needed);
    for (const input of this.#inputs('[data-design-sleeve-field]')) {
      input.disabled = !needed;
      input.required = needed;
    }

    // Last step of every path through this module, so the property names
    // always match what is enabled right now.
    syncPropertyNames(this);
  }

  /**
   * @param {string} selector
   * @returns {NodeListOf<HTMLInputElement>}
   */
  #inputs(selector) {
    return /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll(selector));
  }

  /** Echoes the chosen design name next to each group label. */
  #showPicks() {
    for (const group of this.querySelectorAll('.roots-design__group')) {
      const pick = group.querySelector('[data-design-pick]');
      const checked = /** @type {HTMLInputElement | null} */ (group.querySelector('[data-design-field]:checked'));
      if (pick) pick.textContent = checked?.dataset.designDisplay || checked?.value || '';
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if ('designMode' in target.dataset) this.sync();
    else if ('designField' in target.dataset) {
      this.#showPicks();
      this.#syncSleeve();
    }
  };
}

if (!customElements.get('roots-blouse-design')) {
  customElements.define('roots-blouse-design', RootsBlouseDesign);
}
